/* eslint-env node */

import WGS84Service from './wgs84ProjectionService';

export default {
  getBoundsAndRotation(anchors, width, height) {
    const screenAngle12 = WGS84Service.angleBetween(anchors[0].screenPoint, anchors[1].screenPoint);
    const worldAngle12 = WGS84Service.bearingFromTo(anchors[0].coordinate, anchors[1].coordinate);

    const distance12 = WGS84Service.distanceBetweenWgs84(anchors[0].coordinate, anchors[1].coordinate);
    const screenDistance12 = Math.sqrt(
      Math.pow(anchors[0].screenPoint.x - anchors[1].screenPoint.x, 2) +
        Math.pow(anchors[0].screenPoint.y - anchors[1].screenPoint.y, 2)
    );

    // TOP LEFT (0/0)
    const topLeft = this.wgs84({ x: 0, y: 0 }, anchors, screenAngle12, worldAngle12, distance12, screenDistance12);
    // TOP RIGHT (x/0)
    const topRight = this.wgs84({ x: width, y: 0 }, anchors, screenAngle12, worldAngle12, distance12, screenDistance12);
    // BOTTOM LEFT (0/y)
    const bottomLeft = this.wgs84(
      { x: 0, y: height },
      anchors,
      screenAngle12,
      worldAngle12,
      distance12,
      screenDistance12
    );
    // BOTTOM RIGHT (x/y)
    const bottomRight = this.wgs84(
      { x: width, y: height },
      anchors,
      screenAngle12,
      worldAngle12,
      distance12,
      screenDistance12
    );
    // CENTER (half x / half y)
    const centerPoint = { x: width / 2, y: height / 2 };
    const center = {
      lat: (topLeft.lat + bottomRight.lat) * 0.5,
      lon: (bottomLeft.lon + topRight.lon) * 0.5,
    };

    const centerDistance = WGS84Service.distanceBetweenWgs84(center, topRight);
    const screenAngle = WGS84Service.angleBetween(centerPoint, { x: width, y: 0 });
    const derotatedTopRight = WGS84Service.wgs84FromWithBearingAndDistance(center, screenAngle, centerDistance);

    // to find out the image rotation, compare the "real" top-right coordinate with the unrotated top-right coordinate
    const angleNC = WGS84Service.bearingFromTo(derotatedTopRight, center);
    const angleRC = WGS84Service.bearingFromTo(topRight, center);
    // because the HTML5 canvas-rotation is clockwise rather than counterclockwise, invert the angle
    let rotationAngle = -(angleNC - angleRC);

    const north = Math.max(topRight.lat, Math.max(bottomLeft.lat, Math.max(topLeft.lat, bottomRight.lat)));
    const south = Math.min(topRight.lat, Math.min(bottomLeft.lat, Math.min(topLeft.lat, bottomRight.lat)));
    const east = Math.max(topRight.lon, Math.max(bottomLeft.lon, Math.max(topLeft.lon, bottomRight.lon)));
    const west = Math.min(topRight.lon, Math.min(bottomLeft.lon, Math.min(topLeft.lon, bottomRight.lon)));

    const sizeAfterRotation = this.rotatedDimensions(width, height, rotationAngle);
    return { north: north, south: south, east: east, west: west, rotation: rotationAngle, size: sizeAfterRotation };
  },

  rotatedDimensions(width, height, rotationAngle) {
    const newXRotatedWidth = width * Math.cos(rotationAngle);
    const newXRotatedHeight = height * Math.sin(rotationAngle);
    const rotatedWidth = Math.abs(newXRotatedWidth) + Math.abs(newXRotatedHeight);

    const newYRotatedWidth = width * Math.sin(rotationAngle);
    const newYRotatedHeight = height * Math.cos(rotationAngle);
    const rotatedHeight = Math.abs(newYRotatedWidth) + Math.abs(newYRotatedHeight);

    return { width: rotatedWidth, height: rotatedHeight };
  },

  wgs84(screenPoint, anchors, screenAngle12, worldAngle12, distance12, screenDistance12) {
    const screenAngleToPoint = WGS84Service.angleBetween(anchors[0].screenPoint, screenPoint);
    const worldAngleToPoint = worldAngle12 - (screenAngle12 - screenAngleToPoint);
    const screenDistanceToPoint = Math.sqrt(
      Math.pow(anchors[0].screenPoint.x - screenPoint.x, 2) + Math.pow(anchors[0].screenPoint.y - screenPoint.y, 2)
    );
    const worldDistanceToPoint = (distance12 / screenDistance12) * screenDistanceToPoint;
    const wgs84 = WGS84Service.wgs84FromWithBearingAndDistance(
      anchors[0].coordinate,
      worldAngleToPoint,
      worldDistanceToPoint
    );
    return wgs84;
  },
};
